import { AutoField, FieldLabel } from "@measured/puck";
import { Box, useTheme } from "@mui/material";
import { t } from "i18next";
import ToggleOn from "@mui/icons-material/ToggleOn";
import Tag from "@mui/icons-material/Tag";
import FormatListNumbered from "@mui/icons-material/FormatListNumbered";
import TextFields from "@mui/icons-material/TextFields";
import AlignHorizontalLeft from "@mui/icons-material/AlignHorizontalLeft";

import type { PdfFooterConfig } from "src/components/Puck/config/root";
import NativePuckTitleField from "src/components/Puck/config/fields/NativePuckTitleField";
import { TokenizedTextField } from "src/components/Puck/config/fields/TokenizedTextField";

type PdfFooterFieldProps = {
    value?: PdfFooterConfig;
    onChange: (value: PdfFooterConfig) => void;
    readOnly?: boolean;
};

export const PdfFooterField = ({ value, onChange, readOnly }: PdfFooterFieldProps) => {
    const theme = useTheme();
    const footer = value ?? {};

    const updateField = <K extends keyof PdfFooterConfig>(key: K, fieldValue: PdfFooterConfig[K]) => {
        onChange({ ...footer, [key]: fieldValue });
    };

    return (
        <Box>
            <NativePuckTitleField
                label={t("puck.pdf_footer.title", "Footer")}
                subtitle={t("puck.pdf_footer.subtitle", "Configure PDF footer with page numbers and text")}
                icon="edit"
            />
            <Box sx={{ pt: theme.spacing(3) }}>
                <FieldLabel label={t("puck.pdf_footer.enabled", "Enable footer")} icon={<ToggleOn fontSize="small" />}>
                    <AutoField
                        field={{
                            type: "radio",
                            options: [
                                { label: t("common.yes", "Yes"), value: true },
                                { label: t("common.no", "No"), value: false },
                            ],
                        }}
                        onChange={(v) => updateField("enabled", v as boolean)}
                        value={footer.enabled ?? false}
                        readOnly={readOnly}
                    />
                </FieldLabel>
            </Box>

            {footer.enabled && (
                <>
                    <Box sx={{ pt: theme.spacing(3) }}>
                        <FieldLabel
                            label={t("puck.pdf_footer.show_page_numbers", "Show page numbers")}
                            icon={<Tag fontSize="small" />}
                        >
                            <AutoField
                                field={{
                                    type: "radio",
                                    options: [
                                        { label: t("common.yes", "Yes"), value: true },
                                        { label: t("common.no", "No"), value: false },
                                    ],
                                }}
                                onChange={(v) => updateField("showPageNumbers", v as boolean)}
                                value={footer.showPageNumbers ?? true}
                                readOnly={readOnly}
                            />
                        </FieldLabel>
                    </Box>

                    {footer.showPageNumbers !== false && (
                        <Box sx={{ pt: theme.spacing(3) }}>
                            <FieldLabel
                                label={t("puck.pdf_footer.page_format", "Page number format")}
                                icon={<FormatListNumbered fontSize="small" />}
                            >
                                <AutoField
                                    field={{
                                        type: "radio",
                                        options: [
                                            { label: t("puck.pdf_footer.page_x", "Page X"), value: "page" },
                                            {
                                                label: t("puck.pdf_footer.page_x_of_y", "Page X of Y"),
                                                value: "pageOfTotal",
                                            },
                                        ],
                                    }}
                                    onChange={(v) =>
                                        updateField("pageNumberFormat", v as PdfFooterConfig["pageNumberFormat"])
                                    }
                                    value={footer.pageNumberFormat ?? "pageOfTotal"}
                                    readOnly={readOnly}
                                />
                            </FieldLabel>
                        </Box>
                    )}

                    <Box sx={{ pt: theme.spacing(3) }}>
                        <FieldLabel
                            label={t("puck.pdf_footer.text", "Footer text")}
                            icon={<TextFields fontSize="small" />}
                        >
                            <TokenizedTextField
                                onChange={(v) => updateField("text", v)}
                                value={footer.text ?? ""}
                                placeholder={t(
                                    "puck.pdf_footer.text_placeholder",
                                    "Enter footer text (use { for tokens)",
                                )}
                                readOnly={readOnly}
                            />
                        </FieldLabel>
                    </Box>

                    <Box sx={{ pt: theme.spacing(3) }}>
                        <FieldLabel
                            label={t("puck.pdf_footer.alignment", "Alignment")}
                            icon={<AlignHorizontalLeft fontSize="small" />}
                        >
                            <AutoField
                                field={{
                                    type: "radio",
                                    options: [
                                        { label: t("common.left", "Left"), value: "left" },
                                        { label: t("common.center", "Center"), value: "center" },
                                        { label: t("common.right", "Right"), value: "right" },
                                        {
                                            label: t("puck.pdf_footer.spread", "Spread (text left, page right)"),
                                            value: "spread",
                                        },
                                    ],
                                }}
                                onChange={(v) => updateField("alignment", v as PdfFooterConfig["alignment"])}
                                value={footer.alignment ?? "spread"}
                                readOnly={readOnly}
                            />
                        </FieldLabel>
                    </Box>
                </>
            )}
        </Box>
    );
};

export default PdfFooterField;
